import {
  CoreApi,
  LegsSystem,
  PlannerCoreApi,
  Product,
  SelectedLegComponent,
  TopPanelSystem,
  frameIsBelowTopLevelLimit
} from '@inter-ikea-storageone/range';
import { MeasurementUtils, Vector } from '@inter-ikea-core/core';
import { Point, AutoFillRecommendation } from './AutoFillAlgorithm';
import { reportAutoFillRecommendationChoice } from 'analytics/reporters';
import * as R from 'ramda';

export function addFramesToScene(coreApi: CoreApi, result: AutoFillRecommendation) {
  result.frames.forEach(frameResult => {
    const newProduct = coreApi
      .getServices()
      .productManager.createEntity(frameResult.frame.coreProductId);

    if (newProduct) {
      coreApi.placementService.snapToPosition(
        newProduct,
        new Vector(
          frameResult.position.x + frameResult.frame.communicatedDimensions.width / 2,
          frameResult.position.y + frameResult.frame.communicatedDimensions.height / 2,
          0
        ),
        true
      );
    }
  });
}

export function isPointInsidePolygon(point: Point, polygon: Point[]): boolean {
  const x = point.x;
  const y = point.y;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const ix = polygon[i].x < 0 ? polygon[i].x - 1 : polygon[i].x + 1;
    const iy = polygon[i].y;
    const jx = polygon[j].x < 0 ? polygon[j].x : polygon[j].x + 1;
    const jy = polygon[j].y;

    const intersect = iy > y !== jy > y && x < ((jx - ix) * (y - iy)) / (jy - iy) + ix;

    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Checks if a frame is outside of the wall shape
 * @param point The frame staring point which is the lower left corner X Y coordinate.
 * @param polygon The wall shape polygon
 * @param frame Current frame
 * @param topHeight Height of tops. We need to take account of tops when checking if the frame fits in the wall shape.
 * @returns True if frame is outside the wall shape polygon
 */
export function frameIsOutsidePolygon(
  point: Point,
  polygon: Point[],
  frame: Product,
  topHeight: number
): boolean {
  const { width, height } = frame.communicatedDimensions;
  const shouldAddRoomForTop = frameIsBelowTopLevelLimit(new Vector(point.x, point.y, 0), height);

  const frameEdges = [
    { x: point.x, y: point.y + height + (shouldAddRoomForTop ? topHeight : 0) },
    { x: point.x + width, y: point.y + height + (shouldAddRoomForTop ? topHeight : 0) },
    { x: point.x, y: point.y },
    { x: point.x + width, y: point.y }
  ];
  return frameEdges.filter(p => !isPointInsidePolygon(p, polygon)).length > 0;
}

export const drawPolygon = (context: CanvasRenderingContext2D, polygon: Point[]) => {
  context.beginPath();
  context.moveTo(polygon[0].x, polygon[0].y);
  for (let i = 1; i < polygon.length; i++) {
    context.lineTo(polygon[i].x, polygon[i].y);
  }
  context.closePath();
  context.stroke();
};

export const drawFrames = (
  context: CanvasRenderingContext2D,
  AutoFillRecommendation: AutoFillRecommendation,
  strokeStyle: string,
  fillStyle: string
) => {
  // Draw the frames on the canvas
  AutoFillRecommendation.frames.forEach(({ frame, position }) => {
    context.fillStyle = fillStyle;
    context.fillRect(
      position.x,
      position.y,
      frame.communicatedDimensions.width,
      frame.communicatedDimensions.height
    );
    context.strokeStyle = strokeStyle;
    context.beginPath();
    context.rect(
      position.x,
      position.y,
      frame.communicatedDimensions.width,
      frame.communicatedDimensions.height
    );
    context.stroke();
  });
};

export const compareAutoFillResults = (
  a: AutoFillRecommendation,
  b: AutoFillRecommendation
): boolean => {
  return R.equals(a, b);
};

//If first result (mixed frames) equals one of the others, do not show it
export const removeMixedRecommedationIfItMatchesOneOfTheOther = (
  results: AutoFillRecommendation[]
): AutoFillRecommendation[] => {
  if (results.length !== 3) return results;

  const mixedResultsEqualsOneOfTheOthers =
    compareAutoFillResults(results[0], results[1]) ||
    compareAutoFillResults(results[0], results[2]);

  return mixedResultsEqualsOneOfTheOthers ? results.slice(1) : results;
};

export const measureLegHeight = (coreApi: CoreApi, legProductRef: string): number | undefined => {
  const tempLeg = coreApi.getServices().productManager.createEntity(legProductRef);
  if (!tempLeg) return 0;
  const measurement = MeasurementUtils.getOutsideMeasurementSingle(tempLeg);
  coreApi.getServices().entityManager.disposeEntity(tempLeg);
  return measurement?.height;
};

/**
 *
 * @param AutoFillRecommendation Order search result frames into columns based on X position
 * @returns
 */
export const orderAutoFillRecommendationFramesInColumns = (
  AutoFillRecommendation: AutoFillRecommendation | undefined
) => {
  const orderedInColumns = AutoFillRecommendation?.frames.reduce((agg, f) => {
    const key = `${f.position.x}`;
    if (!agg.get(key)) {
      agg.set(key, [f]);
    } else {
      agg.get(key)?.push(f);
    }
    return agg;
  }, new Map() as Map<string, { frame: Product; position: Point }[]>);

  return orderedInColumns;
};

/**
 * Get frames that should have tops.
 * @param framesInColumns Result from orderAutoFillRecommendationFramesInColumns
 * @param topPanelHeightLimit The height limit for frames to have tops. 1800mm
 * @returns Frame products that should have tops
 */
export const getFramesUnderTopLimit = (
  framesInColumns: Map<string, { frame: Product; position: Point }[]> | undefined,
  topPanelHeightLimit: number
): Product[] => {
  const topFrames = [] as Product[];
  framesInColumns?.forEach(frameList => {
    const sortedColumn = frameList.sort((a: any, b: any) => b.position.y - a.position.y);
    const topFrame = sortedColumn[0];

    if (topFrame.position.y + topFrame.frame.communicatedDimensions.height < topPanelHeightLimit) {
      topFrames.push(topFrame.frame);
    }
  });

  return topFrames;
};

export const removeLegsHeightFromWallShape = (
  wallShape: Vector[] | undefined,
  legHeight: number
): Vector[] => {
  if (wallShape) {
    //Move bottom edges upwards one leg height
    wallShape = wallShape.map(
      c =>
        ({
          ...c,
          Y: c.Y > 0 ? Math.max(c.Y - (legHeight ? legHeight : 0), 0) : c.Y
        } as Vector)
    );
  }

  return wallShape || [];
};

export const getProductHeight = (coreProductId: string, allProducts: Product[]): number => {
  const productHeight = allProducts.find(p => p.coreProductId === coreProductId)
    ?.communicatedDimensions.height;
  return productHeight ? productHeight : 0;
};

export const getTopHeight = (coreApi: CoreApi, allProducts: Product[]) => {
  const allTops = coreApi.products.getTops(allProducts);
  return allTops.length > 0 ? getProductHeight(allTops[0].coreProductId, allProducts) : 0;
};

export const getSelectedLegOrDefault = (coreApi: CoreApi, products: Product[]) => {
  const scene = coreApi.entities.getScene();
  const selectedLegComponent = scene?.getComponent<SelectedLegComponent>(SelectedLegComponent.id);
  return products.find(
    p =>
      p.coreProductId ===
      (selectedLegComponent?.coreProductId
        ? selectedLegComponent.coreProductId
        : coreApi.products.getDefaultLeg())
  );
};

export const setSelectedLegToScene = (coreApi: CoreApi, coreProductId: string) => {
  const system = coreApi.getSystem<LegsSystem>('LegsSystem');
  if (!system) {
    return;
  }
  const scene = coreApi.entities.getScene();
  scene?.addComponent(new SelectedLegComponent(coreProductId));

  system.updateLegsWithHistory(coreProductId);
};

export const setRecommendationToScene = (
  coreApi: PlannerCoreApi,
  autoFillRecommendation: AutoFillRecommendation
) => {
  // Temporarily disable adding top panels. Activate it again after we add
  // frames (if it was enabled before)
  const topPanelSystem = coreApi.getSystem<TopPanelSystem>('TopPanelSystem');
  const prevTopPanelSystemEnabled = topPanelSystem?.isEnabled;

  // Only disable system if it was enabled
  if (prevTopPanelSystemEnabled) {
    topPanelSystem?.disable();
  }

  // Reset entities in the scene
  coreApi.entities.getAll().forEach(e => coreApi.entities.delete(e));

  addFramesToScene(coreApi, autoFillRecommendation);

  // Only enable top panels if it was previously enabled
  if (prevTopPanelSystemEnabled) {
    topPanelSystem?.enable();
  }
};

const cleanRecommendationDataForAnalytics = (
  autoFillRecommendation: AutoFillRecommendation
): any => {
  return {
    ...autoFillRecommendation,
    frames: autoFillRecommendation.frames.map(
      f =>
        ({
          frame: {
            dimensions: f.frame.communicatedDimensions,
            coreProductId: f.frame.coreProductId
          },
          position: f.position
        } as any)
    ),
    type: autoFillRecommendation.type?.toString().replace('translations.', '')
  };
};

export const reportAutoFillRecommendation = (
  autoFillRecommendation: AutoFillRecommendation,
  highLegs: boolean,
  depth: number
) => {
  reportAutoFillRecommendationChoice({
    autoFillRecommendation: cleanRecommendationDataForAnalytics(autoFillRecommendation),
    highLegs,
    solutionDepth: depth
  });
};
